<script setup>
/* global axios */
import { ref, computed, watch } from 'vue';
import { useRoute } from 'vue-router';
import ActivityFilters from './ActivityFilters.vue';
import ActivityItem from './ActivityItem.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  contactId: {
    type: [Number, String],
    required: true,
  },
});

const route = useRoute();

const accountId = computed(() => route.params.accountId);

// State
const activities = ref([]);
const currentPage = ref(1);
const totalPages = ref(1);
const totalCount = ref(0);
const isLoading = ref(false);
const isLoadingMore = ref(false);
const hasError = ref(false);
const currentFilter = ref('all');

// Filter configuration
const FILTER_OPTIONS = [
  { key: 'all', label: 'Todos' },
  { key: 'conversion', label: 'Conversoes' },
  { key: 'form_submission', label: 'Formularios' },
  { key: 'page_view', label: 'Visualizações' },
  { key: 'contact_lifecycle_change', label: 'Ciclo de vida' },
  { key: 'deal_stage_change', label: 'Negocios' },
  { key: 'custom', label: 'Personalizado' },
];

// Computed
const filteredActivities = computed(() => {
  if (currentFilter.value === 'all') return activities.value;

  // Group some filters that map to multiple activity_type values
  const filterGroups = {
    deal_stage_change: [
      'deal_stage_change',
      'deal_created',
      'deal_won',
      'deal_lost',
    ],
  };

  const types = filterGroups[currentFilter.value] || [currentFilter.value];
  return activities.value.filter(a => types.includes(a.activity_type));
});

const hasMorePages = computed(() => currentPage.value < totalPages.value);

const isEmpty = computed(
  () => !isLoading.value && activities.value.length === 0
);

const isFilterEmpty = computed(
  () =>
    !isLoading.value &&
    activities.value.length > 0 &&
    filteredActivities.value.length === 0
);

const filterOptions = computed(() => {
  return FILTER_OPTIONS.map(filter => {
    if (filter.key === 'all') {
      return { ...filter, count: activities.value.length };
    }
    const filterGroups = {
      deal_stage_change: [
        'deal_stage_change',
        'deal_created',
        'deal_won',
        'deal_lost',
      ],
    };
    const types = filterGroups[filter.key] || [filter.key];
    const count = activities.value.filter(a =>
      types.includes(a.activity_type)
    ).length;
    return { ...filter, count };
  });
});

// Methods
const buildUrl = page => {
  return `/api/v1/accounts/${accountId.value}/contacts/${props.contactId}/activities?page=${page}`;
};

const fetchActivities = async (page = 1, append = false) => {
  if (!props.contactId || !accountId.value) return;

  if (append) {
    isLoadingMore.value = true;
  } else {
    isLoading.value = true;
    hasError.value = false;
  }

  try {
    const response = await axios.get(buildUrl(page));
    const { payload, meta } = response.data;

    if (append) {
      activities.value = [...activities.value, ...payload];
    } else {
      activities.value = payload || [];
    }

    currentPage.value = meta?.current_page || page;
    totalPages.value = meta?.total_pages || 1;
    totalCount.value = meta?.total_count || 0;
  } catch (error) {
    hasError.value = true;
    if (!append) {
      activities.value = [];
    }
  } finally {
    isLoading.value = false;
    isLoadingMore.value = false;
  }
};

const loadMore = () => {
  if (hasMorePages.value && !isLoadingMore.value) {
    fetchActivities(currentPage.value + 1, true);
  }
};

const onFilterChange = filter => {
  currentFilter.value = filter;
};

const retry = () => {
  currentPage.value = 1;
  fetchActivities(1);
};

// Watch contactId to refetch
watch(
  () => props.contactId,
  newId => {
    if (newId) {
      currentFilter.value = 'all';
      currentPage.value = 1;
      activities.value = [];
      fetchActivities(1);
    }
  },
  { immediate: true }
);
</script>

<template>
  <div class="flex flex-col">
    <!-- Filters -->
    <div class="px-3 pt-3 pb-2">
      <ActivityFilters
        :current-filter="currentFilter"
        :filters="filterOptions"
        @update:filter="onFilterChange"
      />
    </div>

    <!-- Loading state -->
    <div
      v-if="isLoading"
      class="flex items-center justify-center py-8 text-n-slate-11"
    >
      <Spinner />
    </div>

    <!-- Error state -->
    <div
      v-else-if="hasError"
      class="flex flex-col items-center justify-center py-8 gap-2"
    >
      <span class="i-lucide-alert-circle size-6 text-n-slate-10" />
      <p class="text-sm text-n-slate-10 mb-0">
        Erro ao carregar atividades.
      </p>
      <button
        class="text-xs text-woot-500 hover:text-woot-600 font-medium border-0 bg-transparent cursor-pointer"
        @click="retry"
      >
        Tentar novamente
      </button>
    </div>

    <!-- Empty state -->
    <div
      v-else-if="isEmpty"
      class="flex flex-col items-center justify-center py-8 gap-1.5"
    >
      <span class="i-lucide-activity size-6 text-n-slate-9" />
      <p class="text-sm text-n-slate-10 mb-0">
        Nenhuma atividade registrada.
      </p>
    </div>

    <!-- Filter empty state -->
    <div
      v-else-if="isFilterEmpty"
      class="flex flex-col items-center justify-center py-6 gap-1"
    >
      <span class="i-lucide-filter-x size-5 text-n-slate-9" />
      <p class="text-xs text-n-slate-10 mb-0">
        Nenhuma atividade nesta categoria.
      </p>
    </div>

    <!-- Activities list -->
    <div
      v-else
      class="flex flex-col max-h-[400px] overflow-y-auto"
    >
      <ActivityItem
        v-for="activity in filteredActivities"
        :key="activity.id"
        :activity="activity"
      />

      <!-- Load more -->
      <div
        v-if="hasMorePages"
        class="flex justify-center py-3"
      >
        <button
          class="flex items-center gap-1.5 text-xs font-medium text-woot-500 hover:text-woot-600 border-0 bg-transparent cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="isLoadingMore"
          @click="loadMore"
        >
          <Spinner v-if="isLoadingMore" class="size-3" />
          <span class="i-lucide-chevrons-down size-3.5" v-else />
          <span>Carregar mais</span>
        </button>
      </div>
    </div>

    <!-- Activity count footer -->
    <div
      v-if="!isLoading && !isEmpty && totalCount > 0"
      class="px-3 py-1.5 text-[10px] text-n-slate-9 text-center border-t border-n-weak"
    >
      {{ filteredActivities.length }} de {{ totalCount }} atividades
    </div>
  </div>
</template>
